import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useProject } from '../context/ProjectContext';
import { tabOrder, tabDescriptions, tabPrompts } from '../data/checklistTemplates';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiCheck, FiCircle, FiCheckCircle, FiTarget, FiBook, FiHeart } = FiIcons;

const ChecklistWorkflow = () => {
  const { id } = useParams();
  const { getProject, getChecklistItems, updateChecklistItem, markProjectAsPublished } = useProject();
  const [activeTab, setActiveTab] = useState(tabOrder[0]);
  const [project, setProject] = useState(null);

  useEffect(() => {
    const projectData = getProject(id);
    setProject(projectData);
  }, [id, getProject]);

  const checklistItems = getChecklistItems(id, activeTab);
  const allItems = getChecklistItems(id);
  const completedItems = allItems.filter(item => item.completed);
  const progress = allItems.length > 0 ? Math.round((completedItems.length / allItems.length) * 100) : 0;

  const handleItemToggle = (itemId, completed) => {
    updateChecklistItem(itemId, { completed });
  };

  const handleMarkAsPublished = () => {
    markProjectAsPublished(id);
  };

  const getTabProgress = (tab) => {
    const tabItems = getChecklistItems(id, tab);
    const completedTabItems = tabItems.filter(item => item.completed);
    return tabItems.length > 0 ? Math.round((completedTabItems.length / tabItems.length) * 100) : 0;
  };

  const isTabComplete = (tab) => {
    return getTabProgress(tab) === 100;
  };

  const isAllComplete = () => {
    return progress === 100;
  };

  if (!project) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <SafeIcon icon={FiBook} className="text-6xl text-gray-400 mx-auto mb-4" />
          <p className="text-xl text-gray-600">Loading project...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Link
            to="/"
            className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-4"
          >
            <SafeIcon icon={FiArrowLeft} className="mr-2" />
            Back to Dashboard
          </Link>
          
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              {project.title}
            </h1>
            {project.subtitle && (
              <p className="text-lg text-gray-600 mb-4">{project.subtitle}</p>
            )}
            
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-6">
                <span className="text-sm font-medium text-gray-600">
                  {project.genre}
                </span>
                {project.secondary_category && (
                   <span className="text-sm font-medium text-gray-600">
                     • {project.secondary_category}
                   </span>
                )}
                {project.target_release_date && (
                  <span className="text-sm text-gray-600">
                    Target: {new Date(project.target_release_date).toLocaleDateString()}
                  </span>
                )}
              </div>
              
              <div className="flex items-center space-x-4">
                <div className="text-right">
                  <div className="text-2xl font-bold text-blue-600">{progress}%</div>
                  <div className="text-sm text-gray-600">Complete</div>
                </div>
                <div className="w-32 bg-gray-200 rounded-full h-3">
                  <div
                    className="bg-gradient-to-r from-blue-500 to-blue-600 h-3 rounded-full transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Tab Navigation */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-1"
          >
            <div className="bg-white rounded-lg shadow-lg p-4 sticky top-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Publishing Steps</h3>
              <nav className="space-y-2">
                {tabOrder.map((tab, index) => {
                  const tabProgress = getTabProgress(tab);
                  const isComplete = isTabComplete(tab);
                  const isActive = activeTab === tab;
                  
                  return (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`w-full text-left p-3 rounded-lg transition-colors duration-200 ${
                        isActive 
                          ? 'bg-blue-100 border-l-4 border-blue-600' 
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-sm font-medium ${
                          isActive ? 'text-blue-800' : 'text-gray-700'
                        }`}>
                          {index + 1}. {tab}
                        </span>
                        {isComplete && (
                          <SafeIcon icon={FiCheckCircle} className="text-green-500" />
                        )}
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-1">
                        <div
                          className={`h-1 rounded-full transition-all duration-300 ${
                            isComplete ? 'bg-green-500' : 'bg-gradient-to-r from-blue-500 to-blue-600'
                          }`}
                          style={{ width: `${tabProgress}%` }}
                        ></div>
                      </div>
                    </button>
                  );
                })}
              </nav>
            </div>
          </motion.div>

          {/* Main Content */}
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-3"
          >
            <div className="bg-white rounded-lg shadow-lg p-8">
              {/* Tab Header */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-2">
                  {activeTab}
                </h2>
                <p className="text-lg text-gray-600 mb-4">
                  {tabDescriptions[activeTab]}
                </p>
                
                {/* Tab Prompts */}
                {tabPrompts[activeTab] && (
                  <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg">
                    <div className="flex items-start">
                      <SafeIcon icon={FiHeart} className="text-blue-400 mr-3 mt-1" />
                      <div>
                        <h4 className="font-semibold text-blue-800 mb-2">GospelWise Guidance</h4>
                        {tabPrompts[activeTab].map((prompt, index) => (
                          <p key={index} className="text-blue-700 italic mb-1">
                            "{prompt}"
                          </p>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Checklist Items */}
              <div className="space-y-4">
                <AnimatePresence mode="wait">
                  {checklistItems.map((item, index) => (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className={`p-4 border rounded-lg cursor-pointer transition-all duration-200 ${
                        item.completed 
                          ? 'bg-green-50 border-green-200' 
                          : 'bg-gray-50 border-gray-200 hover:border-blue-300'
                      }`}
                      onClick={() => handleItemToggle(item.id, !item.completed)}
                    >
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0 mt-1">
                          <SafeIcon 
                            icon={item.completed ? FiCheckCircle : FiCircle} 
                            className={`text-xl ${
                              item.completed ? 'text-green-500' : 'text-gray-400'
                            }`} 
                          />
                        </div>
                        <div className="flex-1">
                          <h4 className={`font-medium mb-1 ${
                            item.completed ? 'text-green-800 line-through' : 'text-gray-800'
                          }`}>
                            {item.label}
                          </h4>
                          <p className={`text-sm ${
                            item.completed ? 'text-green-600' : 'text-gray-600'
                          }`}>
                            {item.description}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>

              {/* Final Publishing Button */}
              {activeTab === 'Final Publishing Review' && isAllComplete() && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="mt-8 p-6 bg-gradient-to-r from-green-50 to-orange-50 border border-green-200 rounded-lg text-center"
                >
                  <SafeIcon icon={FiTarget} className="text-4xl text-orange-500 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-800 mb-2">
                    Congratulations! Your book is ready to publish.
                  </h3>
                  <p className="text-gray-600 mb-4 italic">
                    "Faithfulness is not perfection — it's intentional stewardship. You are ready."
                  </p>
                  <button
                    onClick={handleMarkAsPublished}
                    className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-8 py-3 rounded-lg font-semibold hover:from-orange-600 hover:to-orange-700 transition-all duration-200"
                  >
                    Mark This Book as Ready to Publish
                  </button>
                </motion.div>
              )}

              {/* Navigation */}
              <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
                <button
                  onClick={() => {
                    const currentIndex = tabOrder.indexOf(activeTab);
                    if (currentIndex > 0) {
                      setActiveTab(tabOrder[currentIndex - 1]);
                    }
                  }}
                  disabled={tabOrder.indexOf(activeTab) === 0}
                  className={`px-6 py-2 rounded-lg font-medium transition-colors duration-200 ${
                    tabOrder.indexOf(activeTab) === 0
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Previous Step
                </button>
                
                <button
                  onClick={() => {
                    const currentIndex = tabOrder.indexOf(activeTab);
                    if (currentIndex < tabOrder.length - 1) {
                      setActiveTab(tabOrder[currentIndex + 1]);
                    }
                  }}
                  disabled={tabOrder.indexOf(activeTab) === tabOrder.length - 1}
                  className={`px-6 py-2 rounded-lg font-medium transition-colors duration-200 ${
                    tabOrder.indexOf(activeTab) === tabOrder.length - 1
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700'
                  }`}
                >
                  Next Step
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ChecklistWorkflow;